import { useState, useEffect } from "react";
import { computeRow, fmt } from "@/lib/format";
import DatePicker from "@/components/DatePicker";
import { X } from "lucide-react";

const emptyRecord = {
  musteri: "",
  tc_no: "",
  aciklama: "",
  urun: "",
  dosya_no: "",
  icra_dairesi: "",
  tarih: new Date().toISOString().slice(0, 10),
  vade_tarihi: "",
  alis_fiyati: "",
  satis_fiyati: "",
  icra_tutari: "",
  tahsilat: "",
  odenen: "",
  asil_alacak: "",
};

const numFields = [
  { key: "alis_fiyati", label: "Alış Fiyatı" },
  { key: "satis_fiyati", label: "Satış Fiyatı" },
  { key: "icra_tutari", label: "İcra Tutarı" },
  { key: "tahsilat", label: "Peşinat / İcra Öncesi Ödenen" },
  { key: "odenen", label: "Ödenen" },
  { key: "asil_alacak", label: "Asıl Alacak" },
];

export default function RecordForm({ initial, onClose, onSubmit }) {
  const [form, setForm] = useState(emptyRecord);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        musteri: initial.musteri || "",
        tc_no: initial.tc_no || "",
        aciklama: initial.aciklama || "",
        urun: initial.urun || "",
        dosya_no: initial.dosya_no || "",
        icra_dairesi: initial.icra_dairesi || "",
        tarih: (initial.tarih || "").slice(0, 10),
        vade_tarihi: (initial.vade_tarihi || "").slice(0, 10),
        alis_fiyati: initial.alis_fiyati ?? "",
        satis_fiyati: initial.satis_fiyati ?? "",
        icra_tutari: initial.icra_tutari ?? "",
        tahsilat: initial.acilis_tahsilat ?? initial.tahsilat ?? "",
        odenen: initial.odenen ?? "",
        asil_alacak: initial.asil_alacak ?? "",
      });
    }
  }, [initial]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const preview = computeRow(form);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      musteri: form.musteri,
      tc_no: form.tc_no || "",
      aciklama: form.aciklama,
      urun: form.urun || "",
      dosya_no: form.dosya_no || "",
      icra_dairesi: form.icra_dairesi || "",
      tarih: form.tarih,
      vade_tarihi: form.vade_tarihi || null,
      alis_fiyati: Number(form.alis_fiyati) || 0,
      satis_fiyati: Number(form.satis_fiyati) || 0,
      icra_tutari: Number(form.icra_tutari) || 0,
      tahsilat: Number(form.tahsilat) || 0,
      odenen: Number(form.odenen) || 0,
      asil_alacak: Number(form.asil_alacak) || 0,
    };
    try {
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div
        className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-2xl my-8"
        data-testid="record-form"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight">
            {initial ? "Kaydı Düzenle" : "Yeni Cari Hesap Kaydı"}
          </h3>
          <button
            onClick={onClose}
            aria-label="Kapat"
            data-testid="record-form-close"
            className="text-[#8C8377] hover:text-[#26231E]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="kicker block mb-1.5">MÜŞTERİ</label>
              <input
                value={form.musteri}
                onChange={(e) => change("musteri", e.target.value)}
                required
                data-testid="form-musteri-input"
                className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
                placeholder="Müşteri / Cari adı"
              />
            </div>
            <div>
              <label className="kicker block mb-1.5">İŞLEM TARİHİ</label>
              <DatePicker
                value={form.tarih}
                onChange={(v) => change("tarih", v)}
                testId="form-tarih-input"
              />
            </div>
            <div>
              <label className="kicker block mb-1.5">VADE TARİHİ</label>
              <DatePicker
                value={form.vade_tarihi}
                onChange={(v) => change("vade_tarihi", v)}
                placeholder="Vade seçin (ops.)"
                testId="form-vade-input"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="kicker block mb-1.5">TC KİMLİK NO</label>
            <input
              value={form.tc_no}
              onChange={(e) => change("tc_no", e.target.value.replace(/\D/g, "").slice(0, 11))}
              inputMode="numeric"
              maxLength={11}
              data-testid="form-tc-no-input"
              className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm tabular-nums focus:outline-none focus:border-[#B8933F]"
              placeholder="Risk eşleştirme için (ops.)"
            />
          </div>

          <div className="mb-4">
            <label className="kicker block mb-1.5">AÇIKLAMA</label>
            <input
              value={form.aciklama}
              onChange={(e) => change("aciklama", e.target.value)}
              data-testid="form-aciklama-input"
              className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
              placeholder="İsteğe bağlı not"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="kicker block mb-1.5">ÜRÜN ADI</label>
              <input
                value={form.urun}
                onChange={(e) => change("urun", e.target.value)}
                data-testid="form-urun-input"
                className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
                placeholder="Ürün / cihaz (ops.)"
              />
            </div>
            <div>
              <label className="kicker block mb-1.5">DOSYA NO</label>
              <input
                value={form.dosya_no}
                onChange={(e) => change("dosya_no", e.target.value)}
                data-testid="form-dosya-no-input"
                className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
                placeholder="Örn. 2026/1150 (ops.)"
              />
            </div>
            <div>
              <label className="kicker block mb-1.5">İCRA DAİRESİ</label>
              <input
                value={form.icra_dairesi}
                onChange={(e) => change("icra_dairesi", e.target.value)}
                data-testid="form-icra-dairesi-input"
                className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
                placeholder="Örn. Bursa 2. İcra (ops.)"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {numFields.map((f) => (
              <div key={f.key}>
                <label className="kicker block mb-1.5">{f.label.toUpperCase()}</label>
                <input
                  type="number"
                  step="0.01"
                  value={form[f.key]}
                  onChange={(e) => change(f.key, e.target.value)}
                  data-testid={`form-${f.key}-input`}
                  className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm tabular-nums focus:outline-none focus:border-[#B8933F]"
                  placeholder="0"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 border border-[#E7DECC] bg-[#F3ECDD] rounded-sm">
            <div className="kicker px-4 py-2 border-b border-[#E7DECC]">OTOMATİK HESAPLANAN</div>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E7DECC]">
              <Calc label="Kalan" value={preview.kalan} />
              <Calc label="Brüt Risk" value={preview.brut_risk} danger />
              <Calc label="Net Risk" value={preview.net_risk} danger />
              <Calc label="Kâr" value={preview.kar} success />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              data-testid="form-cancel-button"
              className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="form-save-button"
              className="px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Calc({ label, value, danger, success, interest }) {
  let color = "text-[#26231E]";
  if (danger && value > 0) color = "text-[#A63A3A]";
  if (success && value >= 0) color = "text-[#2F6E4E]";
  if (interest) color = "text-[#7C5E9B]";
  return (
    <div className="px-4 py-3">
      <div className="kicker mb-1">{label}</div>
      <div className={`font-heading font-bold text-base tabular-nums ${color}`}>{fmt(value)}</div>
    </div>
  );
}
